import { Chip, FormControlLabel, Radio, RadioGroup, Stack, Tooltip } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';
import { MappingCandidate, MetricLookupItem } from '../../../types/syntiro_honey';
import {
  MATCH_STRENGTH_COLOR,
  MATCH_STRENGTH_LABEL,
  describeMatchStrength,
  deriveMatchStrength
} from './match_strength';
import { parseMappingReason } from './mapping_reason';

interface MappingCandidatesPickerProps {
  /** The ranked candidates surfaced by the mapper. */
  candidates: Array<MappingCandidate>;
  /** The metric id currently chosen in the edit form, or null. */
  selectedMetricId: string | null;
  /** Called with the candidate's metric id when the reviewer picks one. */
  onSelect: (metricId: string) => void;
  /** The label exactly as extracted — what this list is trying to map. */
  rawLabel: string;
  /**
   * True when the record carries no metric at all. The mapper then attaches the
   * three NEAREST metrics with no floor rather than leave an empty picker, so the
   * list must say it is a last resort instead of looking like a shortlist.
   */
  unmapped: boolean;
  /**
   * Catalogue detail for the candidates, keyed by metric id. Empty until the
   * lookup resolves, so anything drawn from it renders only once known.
   */
  metricsById: Record<string, MetricLookupItem>;
}

/**
 * The name every candidate shares, or null when they differ.
 *
 * The mapper has a dedicated duplicate-name branch: several catalogue metrics carry
 * the same name and differ only by `standardName` / `metricGroup`. Detecting it from
 * the names themselves rather than from the reason text keeps this independent of
 * the mapper's wording.
 * @param {Array<MappingCandidate>} candidates The ranked candidates.
 * @returns {string | null} The shared name, or null.
 */
export function findSharedCandidateName(candidates: Array<MappingCandidate>): string | null {
  if (candidates.length < 2) {
    return null;
  }
  const first = candidates[0].name.trim();
  const shared = candidates.every(
    (candidate) => candidate.name.trim().toLocaleLowerCase() === first.toLocaleLowerCase()
  );
  return shared ? first : null;
}

/**
 * What this metric expects in the value column, or null while the catalogue lookup
 * has not resolved it. A qualitative metric has no unit — it takes a verdict — so
 * the same slot says that rather than going blank.
 */
function describeExpectedValue(metric: MetricLookupItem | undefined): string | null {
  if (metric === undefined) {
    return null;
  }
  return metric.unit === null ? 'expects Yes/No/Partial' : `expects ${metric.unit}`;
}

interface CandidateCardProps {
  /** The candidate this card offers. */
  candidate: MappingCandidate;
  /** The name shared by every candidate, or null when they differ. */
  sharedName: string | null;
  /** Catalogue detail for this candidate, or undefined while the lookup resolves. */
  metric: MetricLookupItem | undefined;
}

/**
 * One candidate's content: what separates it from its siblings first, then the
 * catalogue placement, then why the mapper offered it.
 * @returns {JSX.Element}
 */
function CandidateCard({ candidate, sharedName, metric }: CandidateCardProps) {
  const strength = deriveMatchStrength(candidate.score);
  const { basis, unitConflict, adjustments } = parseMappingReason(candidate.reason);
  // When every candidate carries the same name, the name separates nothing and the
  // GROUP is the whole decision — so the group leads and the name moves down.
  const heading = sharedName === null ? candidate.name : candidate.metricGroup;
  const placement =
    sharedName === null
      ? `${candidate.standardName} · ${candidate.metricGroup}`
      : `${candidate.standardName} › ${candidate.name}`;
  const expected = describeExpectedValue(metric);

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <BodyText weight="Medium" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
          {heading}
        </BodyText>
        {/* The score itself moves into the tooltip: on the duplicate-name branch
            every candidate carries the SAME score, so printing it per row
            separated nothing while competing with the name for attention. */}
        <Tooltip title={describeMatchStrength(candidate.score)}>
          <Chip
            label={MATCH_STRENGTH_LABEL[strength]}
            role="img"
            aria-label={describeMatchStrength(candidate.score)}
            size="small"
            variant="outlined"
            color={MATCH_STRENGTH_COLOR[strength]}
            sx={{ flexShrink: 0 }}
          />
        </Tooltip>
      </Stack>
      <CaptionText>
        {placement}
        {expected !== null && ` · ${expected}`}
      </CaptionText>
      {/* A unit disagreement is the single most decision-relevant thing the mapper
          can say about a candidate. It arrives last in the composed reason string,
          so it gets lifted out and coloured rather than trailing off in grey. */}
      {unitConflict !== null && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'warning.main' }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
          <CaptionText sx={{ color: 'inherit' }}>{unitConflict}</CaptionText>
        </Stack>
      )}
      <CaptionText>{[basis, ...adjustments].join(' ')}</CaptionText>
    </Stack>
  );
}

interface PickerHeadingProps {
  /** The label being mapped. */
  rawLabel: string;
  /** The name shared by every candidate, or null when they differ. */
  sharedName: string | null;
  /** How many candidates are offered. */
  count: number;
  /** True when the record has no metric, so this list is the no-floor fallback. */
  unmapped: boolean;
}

/** States what the list is and why it is on screen — never just "Mapping candidates". */
function PickerHeading({ rawLabel, sharedName, count, unmapped }: PickerHeadingProps) {
  if (sharedName !== null) {
    return (
      <Stack spacing={0.25}>
        <BodyText weight="Medium">
          {count} metrics are called &ldquo;{sharedName}&rdquo;
        </BodyText>
        <CaptionText>
          The file said &ldquo;{rawLabel}&rdquo;. The name matches — pick which one this record
          belongs to.
        </CaptionText>
      </Stack>
    );
  }
  if (unmapped) {
    return (
      <Stack spacing={0.25}>
        <BodyText weight="Medium">Nothing in the catalogue matched</BodyText>
        <CaptionText>
          No metric is a good fit for &ldquo;{rawLabel}&rdquo;. These are the nearest — check them
          against the source, or search the full catalogue above.
        </CaptionText>
      </Stack>
    );
  }
  return (
    <Stack spacing={0.25}>
      <BodyText weight="Medium">Suggested metrics</BodyText>
      <CaptionText>
        For &ldquo;{rawLabel}&rdquo;. Pick the one that fits, or search the full catalogue above.
      </CaptionText>
    </Stack>
  );
}

/**
 * Ranked metric mapping candidates for one extracted label. The reviewer must pick
 * explicitly — nothing is ever auto-selected.
 *
 * Rendered as a real radio group rather than a list of buttons: the choice is
 * mutually exclusive, so it should be ONE tab stop with arrow-key navigation, and
 * the checked state should be announced.
 * @returns {JSX.Element}
 */
export default function MappingCandidatesPicker({
  candidates,
  selectedMetricId,
  onSelect,
  rawLabel,
  unmapped,
  metricsById
}: MappingCandidatesPickerProps) {
  const sharedName = findSharedCandidateName(candidates);

  return (
    <Stack spacing={1}>
      <PickerHeading
        rawLabel={rawLabel}
        sharedName={sharedName}
        count={candidates.length}
        unmapped={unmapped}
      />
      <RadioGroup
        aria-label="Metric candidates"
        // Empty string leaves every radio unchecked, so nothing is ever pre-selected.
        value={selectedMetricId ?? ''}
        onChange={(_event, metricId: string) => onSelect(metricId)}
      >
        <Stack spacing={0.75}>
          {candidates.map((candidate) => (
            <FormControlLabel
              key={candidate.metricId}
              value={candidate.metricId}
              control={<Radio size="small" sx={{ alignSelf: 'flex-start', pt: 0.25 }} />}
              disableTypography
              label={
                <CandidateCard
                  candidate={candidate}
                  sharedName={sharedName}
                  metric={metricsById[candidate.metricId]}
                />
              }
              sx={{
                alignItems: 'flex-start',
                m: 0,
                px: 1,
                py: 1,
                borderRadius: 1,
                border: 1,
                borderColor: candidate.metricId === selectedMetricId ? 'primary.main' : 'divider',
                bgcolor:
                  candidate.metricId === selectedMetricId ? 'action.selected' : 'transparent',
                // The label holds the whole card, so it must fill the row rather
                // than shrink to its content the way a plain text label would.
                '& .MuiFormControlLabel-label': { width: '100%', minWidth: 0 }
              }}
            />
          ))}
        </Stack>
      </RadioGroup>
    </Stack>
  );
}
